'use client';

import React from 'react';

import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { UI_CONFIG } from '@eliadmin/core/src/constants/ui-config';
import { cn, mergeClassObjects } from '@eliadmin/core/src/utils/style-utils';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';

export type CarouselControlsProps = {
  className?: {
    main?: string;
    prev?: string;
    next?: string;
    button?: string;
  };
  buttonProps?: ButtonProps;
  prevIcon?: React.ReactNode;
  nextIcon?: React.ReactNode;
};

export const CarouselControls = (props: CarouselControlsProps) => {
  const className = mergeClassObjects(props.className, UI_CONFIG.carouselControls?.className);
  const buttonProps = props.buttonProps ?? UI_CONFIG.carouselControls?.buttonProps;
  const prevIcon = props.prevIcon ?? UI_CONFIG.carouselControls?.prevIcon ?? (
    <GoArrowLeft size={20} />
  );
  const nextIcon = props.nextIcon ?? UI_CONFIG.carouselControls?.nextIcon ?? (
    <GoArrowRight size={20} />
  );

  const context = useCarouselContext();
  if (!context) throw new Error('CarouselControls must be used inside CarouselProvider');

  if (!context.canGoPrev && !context.canGoNext) return null;

  return (
    <div className={cn('flex flex-row gap-2', 'ea-carousel-controls', className.main)}>
      <Button
        {...buttonProps}
        aria-label="Predchádzajúca snímka"
        onClick={() => context.api.scrollPrev()}
        disabled={!context.canGoPrev}
        className={cn(
          'max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] cursor-pointer border-none',
          'ea-carousel-controls__button ea-carousel-controls__button--prev',
          className.button,
          className.prev,
        )}
        variant="white"
        size="icon"
      >
        {prevIcon}
      </Button>
      <Button
        {...buttonProps}
        aria-label="Ďalšia snímka"
        onClick={() => context.api.scrollNext()}
        disabled={!context.canGoNext}
        className={cn(
          'max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] cursor-pointer border-none',
          'ea-carousel-controls__button ea-carousel-controls__button--next',
          className.button,
          className.next,
        )}
        variant="dark"
        size="icon"
      >
        {nextIcon}
      </Button>
    </div>
  );
};
